/* eslint-disable indent */
import { usePostHog } from 'posthog-js/react';
import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { EventEmitter } from '~utils/event_emitter';
import type { iJwtAuthContext } from '~utils/jwt';

const POSTHOG_PROJECT_API_KEY = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;

interface iPosthogEvent {
  event: string;
  properties?: object;
}
export const posthogEvent = new EventEmitter<iPosthogEvent>();

function useProductAnalytics() {
  let initialised: boolean = false;
  const enabled = POSTHOG_PROJECT_API_KEY != null;
  const posthog = usePostHog();
  const location = useLocation();

  const postHogOptions = {
    api_host: 'https://d3gcparwh40s87.cloudfront.net',
    ui_host: 'https://eu.posthog.com',
  };

  const initialise = async ({ userId, customer, customerId, tenant, tenantId, tenantType }: iJwtAuthContext) => {
    posthog.register({
      // Set super properties
      'intelliprove.AppName': 'health_dashboard',
      'intelliprove.IsDevKey': tenantType == null ? null : tenantType !== 'production',
      'intelliprove.CustomerId': customerId,
      'intelliprove.Tenant': tenant,
      'intelliprove.TenantId': tenantId,
      'intelliprove.TenantType': tenantType,
    });
    const phUserId = userId || posthog.get_distinct_id();

    posthog.identify(phUserId);
    posthog.people.set({
      $userId: phUserId,
      'intelliprove.UserId': userId,
    });

    if (customerId !== null) {
      posthog.group('customer', customerId.toString(), {
        Name: customer,
      });
    }

    if (tenantId !== null) {
      posthog.group('tenant', tenantId.toString(), {
        Name: tenant,
        Type: tenantType,
      });
    }

    initialised = true;
  };

  const getUserReferenceId = () => {
    if (!initialised) throw new Error('Product analytics service is not yet initialised.');
    return posthog.get_distinct_id();
  };

  useEffect(() => {
    const unsub = posthogEvent.subscribe((evt: iPosthogEvent) => {
      const props = {
        pathname: location.pathname,
        pathkey: location.key,
        ...evt.properties,
      };
      posthog.capture(evt.event, props);
    });

    return () => unsub();
  }, []);

  return {
    analyticsInitialised: initialised,
    analyticsEnabled: enabled,
    initialiseAnalytics: initialise,
    getUserReferenceId,
    postHogApiKey: POSTHOG_PROJECT_API_KEY,
    postHogOptions,
  };
}

export default useProductAnalytics;
